import { useEffect, useState } from "react";

import { Sparkles } from "lucide-react";

import { Button } from "@components/ui/button";

import { useCaseByCaseNumber } from "@hooks/queries/use-cases";

import { onSessionEnd } from "@utils/sessionTeardown";

import { CaseSummaryPanel } from "./CaseSummaryPanel";
import { buildSummarySubtitle } from "./build-summary-subtitle";

/**
 * Case-header button + the case summary panel it opens (MDRS-486).
 *
 * Mounted in the case shell (not a tab) so an open panel stays open across
 * Prep, Scribe and Report Builder, even though the button itself only shows
 * on the case detail page (`showButton`); the shell keys it by case number so
 * switching cases closes it.
 */
export const CaseSummaryLauncher = ({
  caseNumber,
  showButton = true,
}: {
  caseNumber: string;
  showButton?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Logout can leave this tab mounted, so close rather than keep showing the
  // case (the pop-out window closes itself the same way).
  useEffect(() => onSessionEnd(() => setIsOpen(false)), []);
  const { data: caseData } = useCaseByCaseNumber({
    case_number: caseNumber,
    enabled: !!caseNumber,
  });

  return (
    <>
      {showButton && (
        <Button
          size="sm"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <Sparkles size={16} />
          View summary
        </Button>
      )}

      {isOpen && (
        <CaseSummaryPanel
          caseNumber={caseNumber}
          templateId={caseData?.template?.id}
          documentSubtitle={buildSummarySubtitle({
            examineeName: caseData?.examinee_name,
            caseType: caseData?.case_type,
            appointmentDate: caseData?.appointment_date,
            doctorName: caseData?.doctor_name,
          })}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
